import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { adminService } from '@/services/adminService';
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import ReviewModerationPanel from "@/components/admin/ReviewModerationPanel";
import {
  AlertCircle,
  CheckCircle,
  Search
} from "lucide-react";

const ReviewModerationPage = () => {
  const { user, isAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterReported, setFilterReported] = useState(true);

  // Fetch all reviews for admins
  const { data: reviews = [], isLoading, refetch } = useQuery({
    queryKey: ['adminReviews', filterReported],
    queryFn: async () => {
      // Verify admin status first
      if (!isAdmin) {
        throw new Error("Unauthorized");
      }

      const response = await adminService.listReviewsForModeration(filterReported);
      return response.items;
    },
    enabled: !!isAdmin && !!user,
  });

  const handleReviewUpdated = () => {
    refetch();
    toast.success("Review status updated");
  };

  // Filter reviews by search term
  const filteredReviews = reviews.filter(review => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      review.userName.toLowerCase().includes(searchLower) ||
      (review.userEmail && review.userEmail.toLowerCase().includes(searchLower)) ||
      review.comment.toLowerCase().includes(searchLower) ||
      (review.reportedReason && review.reportedReason.toLowerCase().includes(searchLower))
    );
  });

  if (!user || !isAdmin) {
    return (
      <AdminLayout title="Review Moderation">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">
              You need administrator access to view this page.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Review Moderation">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Review Moderation</h1>
          <p className="text-gray-600">
            Manage and moderate user reviews. Filter reported reviews or view all reviews.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
            <div className="relative w-full md:w-1/2">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                type="search"
                placeholder="Search by user, comment, or reason..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex items-center">
              <Button
                variant={filterReported ? "default" : "outline"}
                onClick={() => setFilterReported(true)}
                className="rounded-r-none"
              >
                Reported Only
              </Button>
              <Button
                variant={!filterReported ? "default" : "outline"}
                onClick={() => setFilterReported(false)}
                className="rounded-l-none"
              >
                All Reviews
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-20">
              <div className="animate-spin h-8 w-8 border-4 border-whatsapp border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Loading reviews...</p>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="text-center py-20 border rounded-lg bg-gray-50">
              <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">
                {filterReported ? "No reported reviews" : "No reviews found"}
              </h2>
              <p className="text-gray-500">
                {filterReported 
                  ? "There are no reviews that have been reported by users." 
                  : searchTerm 
                    ? "No reviews match your search criteria." 
                    : "There are no reviews in the system yet."}
              </p>
            </div>
          ) : (
            <ReviewModerationPanel 
              reviews={filteredReviews} 
              onReviewUpdated={handleReviewUpdated} 
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ReviewModerationPage;
